import React, { useState } from 'react';
import apiService from '../services/apiService';
import { Wand2, Image as ImageIcon, FileText, Search, PenTool, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import './MagicExecutorPage.css';

const MagicExecutorPage = () => {
    const [url, setUrl] = useState('');
    const [options, setOptions] = useState({
        rewriteContent: true,
        optimizeSeo: true,
        redesignCss: false,
        generateImage: false,
    });
    const [status, setStatus] = useState('idle'); // idle, loading, success, error
    const [result, setResult] = useState(null);

    const handleOptionChange = (option) => {
        setOptions(prev => ({ ...prev, [option]: !prev[option] }));
    };

    const handleExecute = async () => {
        if (!url) {
            alert('Please enter a target URL first.');
            return;
        }

        setStatus('loading');
        setResult(null);

        try {
            const data = await apiService.executeMagicUrl({
                url: url,
                rewrite_content: options.rewriteContent,
                optimize_seo: options.optimizeSeo,
                redesign_css: options.redesignCss,
                generate_image: options.generateImage
            });
            
            setStatus('success');
            setResult({
                message: data.message || "The Master Orchestrator (Ava) has dispatched the AI Swarm.",
                taskId: data.orchestrator_job_id,
            });
        } catch (error) {
            setStatus('error');
            setResult({ message: error.message || "Failed to initialize the Swarm." });
        }
    };

    return (
        <div className="magic-executor-container">
            <div className="magic-header">
                <div>
                    <h1 className="magic-title"><Wand2 size={28} className="icon-wand"/> Magic Action Console</h1>
                    <p className="magic-subtitle">Drop a WordPress URL and let the Orchestrator delegate tasks to your AI Swarm automatically.</p>
                </div>
            </div>

            <div className="magic-card form-section">
                <div className="input-group">
                    <label>Target Page URL</label>
                    <input 
                        type="url" 
                        className="url-input"
                        placeholder="https://your-wordpress-site.com/page/"
                        value={url}
                        onChange={(e) => setUrl(e.target.value)}
                        disabled={status === 'loading'}
                    />
                </div>

                <div className="options-grid">
                    <label className={`option-card ${options.rewriteContent ? 'selected' : ''}`}>
                        <input type="checkbox" checked={options.rewriteContent} onChange={() => handleOptionChange('rewriteContent')} disabled={status === 'loading'} />
                        <FileText className="option-icon text-blue"/>
                        <span>Rewrite Content (Hyper-Local SEO Architect)</span>
                    </label>

                    <label className={`option-card ${options.optimizeSeo ? 'selected' : ''}`}>
                        <input type="checkbox" checked={options.optimizeSeo} onChange={() => handleOptionChange('optimizeSeo')} disabled={status === 'loading'} />
                        <Search className="option-icon text-green"/>
                        <span>Optimize Yoast Meta (SEO Meta Optimizer)</span>
                    </label>

                    <label className={`option-card ${options.redesignCss ? 'selected' : ''}`}>
                        <input type="checkbox" checked={options.redesignCss} onChange={() => handleOptionChange('redesignCss')} disabled={status === 'loading'} />
                        <PenTool className="option-icon text-purple"/>
                        <span>Modernize Design (Elementor CSS Architect)</span>
                    </label>

                    <label className={`option-card ${options.generateImage ? 'selected' : ''}`}>
                        <input type="checkbox" checked={options.generateImage} onChange={() => handleOptionChange('generateImage')} disabled={status === 'loading'} />
                        <ImageIcon className="option-icon text-orange"/>
                        <span>Generate Featured Image (DALL-E 3 + WebP)</span>
                    </label>
                </div>

                <div className="action-row">
                    <button 
                        className={`btn-launch ${status === 'loading' ? 'loading' : ''}`}
                        onClick={handleExecute}
                        disabled={status === 'loading'}
                    >
                        {status === 'loading' ? (
                            <><Loader2 className="spinner" size={18}/> Orchestrating...</>
                        ) : (
                            <><Wand2 size={18}/> Launch Swarm</>
                        )}
                    </button>
                </div>
            </div>

            {result && (
                <div className={`magic-card result-section ${status}`}>
                    {status === 'success' ? (
                        <>
                            <div className="result-header text-green">
                                <CheckCircle size={24}/> 
                                <h3>Swarm Dispatched</h3>
                            </div>
                            <p>{result.message}</p>
                            {result.taskId && <p className="job-id">Job ID: {result.taskId}</p>}
                            <p className="follow-up-hint">The specific agents are now working in the background. Content will be pushed directly to WordPress when ready.</p>
                        </>
                    ) : (
                        <>
                            <div className="result-header text-red">
                                <AlertCircle size={24}/> 
                                <h3>Execution Failed</h3>
                            </div>
                            <p>{result.message}</p>
                        </>
                    )}
                </div>
            )}
        </div>
    );
};

export default MagicExecutorPage;
